<script setup lang="ts">
  import { type RuleExpression } from 'vee-validate'
  import { type PropType, computed, ref, watch, onMounted, onUnmounted } from 'vue'
  import { DatePicker } from 'v-calendar'
  import { CalendarIcon } from 'lucide-vue-next'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import 'v-calendar/style.css'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: String as PropType<string | null>,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    error: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'validation-state'])
  const opened = ref(false)
  const inputValue = ref<string>(props.modelValue ?? '')
  const datePickerRef = ref(null)

  // Watch for external changes to modelValue
  watch(
    () => props.modelValue,
    (newValue) => {
      inputValue.value = newValue ?? ''
    }
  )

  const handleDateSelect = (date: Date) => {
    const formattedDate = date
      .toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(/\//g, '.')
    emit('update:modelValue', formattedDate)
    inputValue.value = formattedDate
    emit('validation-state', {
      errorMessage: props.error,
      isInvalid: !!props.error
    })
    opened.value = false
  }

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value = target.value

    // Handle empty input
    if (!value) {
      inputValue.value = ''
      emit('update:modelValue', null)
      emit('validation-state', {
        errorMessage: props.error,
        isInvalid: !!props.error
      })
      return
    }

    // Convert YYYY-MM-DD to DD.MM.YYYY
    const [year, month, day] = value.split('-')
    const formattedDate = `${day}.${month}.${year}`
    inputValue.value = formattedDate
    emit('update:modelValue', formattedDate)
    emit('validation-state', {
      errorMessage: props.error,
      isInvalid: !!props.error
    })
  }

  const dateValue = computed(() => {
    if (!props.modelValue) return null
    const [day, month, year] = props.modelValue.split('.')
    return new Date(Number(year), Number(month) - 1, Number(day))
  })

  const isoDateValue = computed(() => {
    if (!props.modelValue) return ''
    const [day, month, year] = props.modelValue.split('.')
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  })
</script>

<template>
  <label
    v-if="label"
    class="mb-2 block text-sm font-medium"
  >
    {{ label }}
  </label>
  <div
    ref="datePickerRef"
    class="relative"
  >
    <div class="relative w-full">
      <input
        v-model="isoDateValue"
        type="date"
        :disabled="disabled"
        class="hide-calendar-icon block w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm focus:z-10"
        :class="[
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-200 focus:border-purple-600 focus:ring-purple-600',
          disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-text bg-white'
        ]"
        @input="handleInput"
      />
      <Popover
        v-model:open="opened"
        :disabled="disabled"
      >
        <PopoverTrigger
          class="absolute left-3 top-2 z-10"
          :disabled="disabled"
        >
          <CalendarIcon
            class="h-5 w-5 text-gray-400"
            :class="{
              'cursor-not-allowed opacity-50': disabled,
              'cursor-pointer hover:text-gray-500': !disabled
            }"
          />
        </PopoverTrigger>
        <PopoverContent
          class="mt-1 w-[calc(100%)] rounded-lg border-0 p-0"
          side="bottom"
          :align="'start'"
          :side-offset="10"
        >
          <DatePicker
            v-model="dateValue"
            :masks="{ input: 'DD.MM.YYYY' }"
            :disabled="disabled"
            :model-config="{ type: 'string', mask: 'DD.MM.YYYY' }"
            locale="de-DE"
            class="w-full"
            @update:model-value="handleDateSelect"
          />
        </PopoverContent>
      </Popover>
    </div>
  </div>
  <p
    v-if="error"
    class="mt-1 text-xs text-red-600"
    :class="{ 'opacity-50': disabled }"
  >
    {{ error }}
  </p>
</template>

<style>
  .hide-calendar-icon::-webkit-calendar-picker-indicator {
    display: none;
  }
</style>
